"use client";

import { useChangePrintingModal } from "modules/decks/stores/useDecksActionsStore";
import { FinishEnum, GameCard } from "modules/cards/model/card";
import { updateCard } from "modules/server/actions/deck";
import { Modal } from "modules/common/components/Modal/Modal";
import { CardChangePrintingCardDetails } from "modules/decks/details/components/DeckInfo/CardGroupSection/CardRow/CardRowActions/CardChangePrintingCardDetails";
import { useInfiniteCardPrintings } from "modules/cards/hooks/useCardPrintings";
import { ResponsiveCardDisplayGrid } from "modules/common/components/grids/ResponsiveCardDisplayGrid";
import { CardSkeleton } from "modules/common/components/CardSkeleton";
import { ResponsiveCardSizing } from "utils/styles";
import { useServerAction } from "modules/common/components/toast/useServerAction";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { QUERIES } from "modules/common/query";
import { useCallback, useMemo } from "react";
import { useHoveredCard } from "modules/decks/stores/useDecksUIStore";

// Module scope, not an inline arrow: ResponsiveCardDisplayGrid's commit effect
// depends on this prop, so a new function per render re-enters that effect on
// every unrelated parent render and can schedule a second startTransition
// while the first commit is still pending.
const printingKey = (c: GameCard) => c.id;

export default function CardChangePrintingDialog() {
  const { changePrintingModal, closeChangePrintingModal } =
    useChangePrintingModal();
  const { open, deckId, deckCard } = changePrintingModal;
  const { hoveredCard, setHoveredCard } = useHoveredCard();

  const queryClient = useQueryClient();
  const { userId } = useAuth();

  const handleClose = useCallback(() => {
    closeChangePrintingModal();
  }, [closeChangePrintingModal]);

  // Set up the server action for updating card printing
  const { execute: executeUpdateCard, isPending } = useServerAction(
    updateCard,
    {
      loadingMessage: deckCard
        ? `Updating printing of ${deckCard.name}...`
        : "Updating printing...",
      showSuccessToast: true,
      showErrorToast: true,
      onSuccess: () => {
        // Invalidate deck queries for printing change
        if (userId && deckId) {
          queryClient.invalidateQueries({
            queryKey: [QUERIES.DECK_DETAILS, deckId],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERIES.DECK_USER],
          });
        }
        handleClose();
      },
      onError: () => {
        // Error is already shown via toast
      },
    }
  );

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isPending: isLoadingPrintings,
    isFetchingNextPage,
  } = useInfiniteCardPrintings(deckCard?.id ?? "", 20);

  // Flatten pages and drop the printing the card already uses.
  const otherPrintings = useMemo(
    () =>
      (data?.pages.flatMap((p) => p.cards) ?? []).filter(
        (card: GameCard) => card.id !== deckCard?.id
      ),
    [data?.pages, deckCard?.id]
  );

  const changePrinting = useCallback(
    (printing: GameCard, finish: FinishEnum) => async (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (!deckId || !deckCard) return;

      await executeUpdateCard(deckId, {
        current: {
          cardId: deckCard.id,
          location: deckCard.location,
          quantity: deckCard.quantity,
          finish: deckCard.finish,
        },
        next: {
          location: deckCard.location,
          quantity: deckCard.quantity,
          cardId: printing.id,
          finish,
        },
      });

      // Keep the hover preview in sync with the chosen printing AND finish.
      if (hoveredCard && hoveredCard.id === deckCard.id) {
        setHoveredCard({ ...printing, finish } as any, true);
      }
    },
    [deckId, deckCard, executeUpdateCard, hoveredCard, setHoveredCard]
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Select printing"
      maxWidth="4xl"
      height="full"
      actions={
        <button className="btn" onClick={handleClose} disabled={isPending}>
          Close
        </button>
      }
    >
      <ResponsiveCardDisplayGrid
        key={deckCard?.id}
        data={otherPrintings}
        isLoading={isLoadingPrintings}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage ?? false}
        isFetchingNextPage={isFetchingNextPage}
        getItemKey={printingKey}
        emptyMessage={
          deckCard
            ? `No other printings for ${deckCard.name}`
            : "No other printings"
        }
        emptyDescription="This card only has one printing or others are not yet available."
        renderItem={(printing) => (
          <CardChangePrintingCardDetails
            card={printing}
            changePrinting={changePrinting}
            disabled={isPending}
            onNavigate={handleClose}
          />
        )}
        renderSkeleton={() => (
          <div style={ResponsiveCardSizing}>
            <CardSkeleton />
          </div>
        )}
      />
    </Modal>
  );
}
